import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Button,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Link as LinkIcon,
  ViewKanban as ViewKanbanIcon,
  List as ListIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import useAuth from './hooks/useAuth';
import Login from './components/Login';
import LinkGenerator from './components/LinkGenerator';
import LinkHistory from './components/LinkHistory';
import DashboardView from './components/DashboardView';
import AllEvaluations from './components/AllEvaluations';
import EvaluationOptionsManager from './components/EvaluationOptionsManager';
const theme = createTheme({
  palette: {
    primary: {
      main: '#2A78B7',
      light: '#3498DB',
      dark: '#2C3E50',
    },
    secondary: {
      main: '#717171',
    },
    success: {
      main: '#39B54A',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 0,
          padding: '10px 24px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
  },
});

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function App() {
  const { currentUser, login, logout, loading } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Login onLogin={login} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <DashboardIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Dashboard de Avaliações
            </Typography>

            {currentUser && (
              <div>
                <Button
                  size="small"
                  aria-controls={open ? 'menu-appbar' : undefined}
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                  startIcon={<AccountCircleIcon />}
                >
                  {currentUser.email}
                </Button>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={open}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleLogout}>
                    <ExitToAppIcon sx={{ mr: 1 }} />
                    Sair
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={0} sx={{ mb: 3 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                icon={<DashboardIcon />}
                label="Visualizar Dados"
                iconPosition="start"
              />
              <Tab
                icon={<ListIcon />}
                label="Todas as Avaliações"
                iconPosition="start"
              />
              <Tab
                icon={<LinkIcon />}
                label="Gerar Links"
                iconPosition="start"
              />
              <Tab
                icon={<ViewKanbanIcon />}
                label="Histórico de Links"
                iconPosition="start"
              />
              <Tab
                icon={<SettingsIcon />}
                label="Opções de Avaliação"
                iconPosition="start"
              />
            </Tabs>
          </Paper>

          <TabPanel value={currentTab} index={0}>
            <DashboardView />
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <AllEvaluations />
          </TabPanel>

          <TabPanel value={currentTab} index={2}>
            <LinkGenerator />
          </TabPanel>

          <TabPanel value={currentTab} index={3}>
            <LinkHistory />
          </TabPanel>

          <TabPanel value={currentTab} index={4}>
            <EvaluationOptionsManager />
          </TabPanel>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
